import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search as SearchIcon, SlidersHorizontal, X, Package, Users, Star, BadgeCheck, MapPin } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { CATEGORIES, ISLANDS, CONDITIONS, DISTANCES, ISLAND_NAME } from "../lib/data";
import { ListingCard } from "../components/ListingCard";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import api, { fileUrl } from "../lib/api";
import { useSeo } from "../lib/useSeo";

function UserResultCard({ u, t }) {
  const display = u.is_professional ? (u.business_name || u.name) : u.name;
  return (
    <Link to={`/user/${u.id}`} data-testid={`user-result-${u.id}`}
      className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-4 hover:border-teal-300 hover:shadow-md transition-all">
      <Avatar className="h-14 w-14">
        <AvatarImage src={fileUrl(u.is_professional ? u.business_logo : u.profile_photo)} />
        <AvatarFallback className="bg-teal-100 text-teal-700 text-lg">{display?.[0]}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm flex items-center gap-1 truncate">{display}{u.is_professional && <BadgeCheck className="h-4 w-4 text-[#008080] shrink-0" />}</p>
        {u.nickname && <p className="text-xs text-slate-400 truncate">@{u.nickname}</p>}
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
          {u.rating > 0 && <span className="flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{u.rating}</span>}
          <span>{u.listing_count} {t("home.listingsCount")}</span>
          {u.island && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{ISLAND_NAME[u.island]}</span>}
        </div>
      </div>
    </Link>
  );
}

export default function Search() {
  const { t, lang } = useI18n();
  const [sp, setSp] = useSearchParams();
  const [entity, setEntity] = useState(sp.get("entity") === "users" ? "users" : "products");
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [f, setF] = useState({
    q: sp.get("q") || "", category: sp.get("category") || "", island: sp.get("island") || "",
    municipality: "", min_price: "", max_price: "", condition: "", seller_type: "",
    sort: "recent",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const munis = ISLANDS.find((i) => i.id === f.island)?.municipalities || [];

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    Object.entries(f).forEach(([k, v]) => {
      if (v !== "" && v !== false && v !== "all") params[k] = v;
    });
    try { const { data } = await api.get("/listings", { params }); setListings(data); }
    catch { setListings([]); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setF((p) => ({ ...p, q: sp.get("q") || "", category: sp.get("category") || "", island: sp.get("island") || "" }));
  }, [sp]);

  useEffect(() => {
    if (entity !== "users") return;
    let active = true;
    setLoadingUsers(true);
    const id = setTimeout(async () => {
      try { const { data } = await api.get("/users/search", { params: { q: f.q } }); if (active) setUsers(data); }
      catch { if (active) setUsers([]); }
      finally { if (active) setLoadingUsers(false); }
    }, 250);
    return () => { active = false; clearTimeout(id); };
  }, [entity, f.q]);

  const clear = () => setF({ q: "", category: "", island: "", municipality: "", min_price: "", max_price: "", condition: "", seller_type: "", sort: "recent" });

  const catName = f.category ? t(`categories.${f.category}`) : "";
  useSeo({
    title: f.category
      ? (lang === "it" ? `${catName} in vendita alle Isole Canarie - Canarias Market`
        : lang === "en" ? `${catName} for sale in the Canary Islands - Canarias Market`
        : `${catName} en venta en las Islas Canarias - Canarias Market`)
      : (lang === "it" ? "Cerca alle Isole Canarie - Canarias Market"
        : lang === "en" ? "Search in the Canary Islands - Canarias Market"
        : "Buscar en las Islas Canarias - Canarias Market"),
    description: f.category
      ? (lang === "it" ? `Annunci di ${catName} in vendita alle Isole Canarie su Canarias Market.`
        : lang === "en" ? `${catName} listings for sale in the Canary Islands on Canarias Market.`
        : `Anuncios de ${catName} en venta en las Islas Canarias en Canarias Market.`)
      : t("services.subtitle"),
  });

  const FilterPanel = () => (
    <div className="space-y-5">
      <div>
        <Label className="text-xs uppercase tracking-wide text-slate-500">{t("search.category")}</Label>
        <Select value={f.category || "all"} onValueChange={(v) => set("category", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-category" className="mt-1.5"><SelectValue placeholder={t("common.all")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("common.all")}</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{t(`categories.${c.id}`)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs uppercase tracking-wide text-slate-500">{t("search.minPrice")}</Label>
          <Input data-testid="filter-min-price" type="number" value={f.min_price} onChange={(e) => set("min_price", e.target.value)} className="mt-1.5" /></div>
        <div><Label className="text-xs uppercase tracking-wide text-slate-500">{t("search.maxPrice")}</Label>
          <Input data-testid="filter-max-price" type="number" value={f.max_price} onChange={(e) => set("max_price", e.target.value)} className="mt-1.5" /></div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide text-slate-500">{t("search.island")}</Label>
        <Select value={f.island || "all"} onValueChange={(v) => { set("island", v === "all" ? "" : v); set("municipality", ""); }}>
          <SelectTrigger data-testid="filter-island" className="mt-1.5"><SelectValue placeholder={t("common.all")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("common.all")}</SelectItem>{ISLANDS.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {f.island && (
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-500">{t("search.municipality")}</Label>
          <Select value={f.municipality || "all"} onValueChange={(v) => set("municipality", v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-municipality" className="mt-1.5"><SelectValue placeholder={t("common.all")} /></SelectTrigger>
            <SelectContent><SelectItem value="all">{t("common.all")}</SelectItem>{munis.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label className="text-xs uppercase tracking-wide text-slate-500">{t("search.distance")}</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {DISTANCES.map((d) => <span key={d} className="px-3 py-1 rounded-full bg-slate-100 text-xs text-slate-600">{d} {t("common.km")}</span>)}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide text-slate-500">{t("search.condition")}</Label>
        <Select value={f.condition || "all"} onValueChange={(v) => set("condition", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-condition" className="mt-1.5"><SelectValue placeholder={t("common.all")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("common.all")}</SelectItem>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{t(`conditions.${c}`)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wide text-slate-500">{t("search.sellerType")}</Label>
        <Select value={f.seller_type || "all"} onValueChange={(v) => set("seller_type", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-seller-type" className="mt-1.5"><SelectValue placeholder={t("common.all")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("common.all")}</SelectItem><SelectItem value="particular">{t("search.private")}</SelectItem><SelectItem value="profesional">{t("search.pro")}</SelectItem></SelectContent>
        </Select>
      </div>
      <Button data-testid="clear-filters-btn" variant="outline" onClick={clear} className="w-full">{t("search.clear")}</Button>
    </div>
  );

  return (
    <div className="px-4 sm:px-8 py-6">
      <div className="flex items-center gap-2 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); const p = new URLSearchParams(sp); if (f.q) p.set("q", f.q); else p.delete("q"); setSp(p); e.currentTarget.querySelector("input")?.blur(); }}
          className="flex-1 flex items-center gap-2">
          <div className="flex-1 flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 h-11">
            <SearchIcon className="h-4 w-4 text-slate-400" />
            <input data-testid="search-page-input" value={f.q} onChange={(e) => set("q", e.target.value)} placeholder={t("search.searchAllPh")} className="flex-1 bg-transparent outline-none text-sm" />
          </div>
          <Button type="submit" data-testid="search-page-submit" className="h-11 px-5 rounded-full bg-[#008080] hover:bg-[#006666] text-white font-semibold shrink-0">{t("common.search")}</Button>
        </form>
        {entity === "products" && (
          <Select value={f.sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger data-testid="sort-select" className="w-auto h-11 rounded-full hidden sm:flex"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">{t("search.recent")}</SelectItem>
              <SelectItem value="price_asc">{t("search.priceAsc")}</SelectItem>
              <SelectItem value="price_desc">{t("search.priceDesc")}</SelectItem>
            </SelectContent>
          </Select>
        )}
        {entity === "products" && (
          <Button data-testid="toggle-filters-btn" variant="outline" onClick={() => setShowFilters((s) => !s)} className="h-11 rounded-full lg:hidden">
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Entity switch: products vs users */}
      <div className="inline-flex p-1 rounded-full bg-slate-100 mb-6">
        <button data-testid="entity-tab-products" onClick={() => setEntity("products")}
          className={`flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold transition-colors ${entity === "products" ? "bg-white text-[#008080] shadow-sm" : "text-slate-500"}`}>
          <Package className="h-4 w-4" />{t("search.products")}
        </button>
        <button data-testid="entity-tab-users" onClick={() => setEntity("users")}
          className={`flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold transition-colors ${entity === "users" ? "bg-white text-[#008080] shadow-sm" : "text-slate-500"}`}>
          <Users className="h-4 w-4" />{t("search.sellers")}
        </button>
      </div>

      {entity === "users" ? (
        <div>
          <p className="text-sm text-slate-500 mb-4">{users.length} {t("search.results")}</p>
          {loadingUsers ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-24 rounded-2xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : users.length === 0 ? (
            <div data-testid="no-users" className="text-center py-20 text-slate-400">{t("search.noUsers")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {users.map((u) => <UserResultCard key={u.id} u={u} t={t} />)}
            </div>
          )}
        </div>
      ) : (
      <div className="flex gap-6">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-5">
            <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" />{t("search.filters")}</h3>
            <FilterPanel />
          </div>
        </aside>

        {showFilters && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilters(false)} />
            <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-5 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg">{t("search.filters")}</h3>
                <button data-testid="close-filters-btn" onClick={() => setShowFilters(false)}><X className="h-5 w-5" /></button>
              </div>
              <FilterPanel />
              <Button onClick={() => setShowFilters(false)} className="w-full mt-4 bg-[#008080] hover:bg-[#006666]">{t("search.apply")}</Button>
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-500 mb-4">{listings.length} {t("search.results")}</p>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[4/5] rounded-2xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : listings.length === 0 ? (
            <div data-testid="no-results" className="text-center py-20 text-slate-400">{t("search.noResults")}</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
